<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from './provider.js';
import { SENDER_TYPES } from './constants';

// Keep in sync with cs-ai-agent's MAX_FEEDBACK_COMMENT_LENGTH (it clamps on ingest).
const MAX_COMMENT_LENGTH = 1000;

const {
  id,
  conversationId,
  content,
  isPrivate,
  sender,
  senderType,
  contentAttributes,
  currentUserId,
} = useMessageContext();
const { t } = useI18n();
const store = useStore();

const isBotAuthored = computed(
  () =>
    sender.value?.type === SENDER_TYPES.AGENT_BOT ||
    senderType.value === SENDER_TYPES.AGENT_BOT
);

// digitt: DENG-7353 / DENG-7574 — matches cs-ai-agent's shadow-note header (see
// apps/cs-ai-agent/src/chatwoot/shadow-note.ts; strings duplicated here because the vendored
// Chatwoot bundle can't import from that app). Two permanent uses: `extractMoniReply` strips the
// header off the body before dropping it into the composer, and (DENG-8313) it is the visibility
// fallback for historical notes that predate the `cs_ai_note_kind` stamp. Since DENG-7570 both
// customer-facing kinds are header + body, and the delegation body IS the would-send customer message.
const SHADOW_NOTE_HEADER =
  /^(?:💬 Respuesta sugerida como nota interna:|👤 Delegación sugerida como nota interna:)\s*/;

// digitt: DENG-8313 — the whole widget (grading + "Usar respuesta") only belongs on customer-facing
// draft notes (`reply`/`delegation`), never on `handoff_briefing` (formatInternalNote's internal
// escalation context) — a click there would paste the reason + summary + transcript to the customer.
// The kind is read from the authoritative `cs_ai_note_kind` stamp (camelCased by MessageList, so
// `csAiNoteKind`). Un-stamped historical notes fall back to matching the shadow header on the body
// (SHADOW_NOTE_HEADER below): reply/delegation notes match and keep the widget; briefing notes don't
// match and are hidden — closing the leak even retroactively. Retire the fallback per apps/cs-ai-agent/CLAUDE.md.
const NOTE_KINDS_WITH_BUTTON = ['reply', 'delegation'];
const buttonAllowed = computed(() => {
  const kind = contentAttributes.value?.csAiNoteKind;
  return kind
    ? NOTE_KINDS_WITH_BUTTON.includes(kind)
    : SHADOW_NOTE_HEADER.test(content.value ?? '');
});

const isVisible = computed(
  () => isPrivate.value && isBotAuthored.value && buttonAllowed.value
);

// MessageList deep-camelCases message content_attributes before this component
// sees them, so the persisted grade arrives as `csAiFeedback` (the PATCH still
// writes snake `cs_ai_feedback` — that's the API + cs-ai-agent webhook contract).
// Reading the camelCased key is what makes the rating/comment survive a reload.
const savedFeedback = computed(() => contentAttributes.value?.csAiFeedback);
const selectedRating = ref(savedFeedback.value?.rating ?? null);
const comment = ref(savedFeedback.value?.comment ?? '');
// Baseline for change-detection; the grade action trims before sending. A ref so
// hasUnsavedComment recomputes when a save updates it (not just when comment does).
const lastSavedComment = ref(comment.value.trim());

// 'idle' | 'saving' | 'saved' | 'error'. A persisted value loads as 'saved' so a
// reopened conversation reads as recorded — the quiet ✓ for later viewers.
const ratingStatus = ref(selectedRating.value ? 'saved' : 'idle');
const commentStatus = ref(lastSavedComment.value ? 'saved' : 'idle');

const hasUnsavedComment = computed(
  () => comment.value.trim() !== lastSavedComment.value
);

// One write carries the FULL cs_ai_feedback: Chatwoot's messages#update shallow-merges
// only at the content_attributes top level, so a partial would drop sibling keys.
// rateAiMessage resolves true/false (never throws) so we can confirm honestly.
const persist = () =>
  store.dispatch('rateAiMessage', {
    conversationId: conversationId.value,
    messageId: id.value,
    rating: selectedRating.value,
    ratedBy: currentUserId.value,
    comment: comment.value,
  });

const rate = async rating => {
  if (selectedRating.value === rating) return;
  selectedRating.value = rating;
  // Re-send the existing comment so flipping the rating never drops it.
  lastSavedComment.value = comment.value.trim();
  ratingStatus.value = 'saving';
  ratingStatus.value = (await persist()) ? 'saved' : 'error';
};

const saveComment = async () => {
  if (!selectedRating.value || !hasUnsavedComment.value) return;
  commentStatus.value = 'saving';
  if (await persist()) {
    lastSavedComment.value = comment.value.trim();
    commentStatus.value = 'saved';
  } else {
    commentStatus.value = 'error';
  }
};

const extractMoniReply = text => (text ?? '').replace(SHADOW_NOTE_HEADER, '').trim();

// digitt: DENG-7353 — "Usar respuesta": drop the bot reply into the composer, reusing the
// Copilot insertion path. Editor type is picked by inbox (rich for web/email).
// digitt: DENG-8011 — additionally capture the click for adoption analytics. The dispatch
// is fire-and-forget AFTER the insertion (recordAiResponseUse resolves true/false, never
// throws), so a failed capture can never block or error the composer insertion above.
const useResponse = () => {
  const inbox = store.getters['inboxes/getInbox'](
    store.getters.getSelectedChat?.inbox_id
  );
  const event = [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(inbox?.channel_type)
    ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
    : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR;
  emitter.emit(event, extractMoniReply(content.value));

  // digitt: DENG-8281 — hand ReplyBox the turn id so it stamps cs_ai_used_turn_id on the
  // agent's outgoing reply (exact "stamp" correlation vs. the monotonic-id window). Emitted
  // AFTER the insertion, which clears any prior stamp, so this set wins (emitter is sync).
  emitter.emit(
    BUS_EVENTS.CS_AI_STAMP_USED_TURN,
    contentAttributes.value?.csAiTurnId
  );

  store
    .dispatch('recordAiResponseUse', {
      conversationId: conversationId.value,
      messageId: id.value,
      usedBy: currentUserId.value,
      usedByName: store.getters.getCurrentUser?.name,
    })
    .catch(() => {});
};
</script>

<template>
  <div v-if="isVisible" class="mt-1">
    <div class="flex items-center justify-between gap-1">
      <div class="flex items-center gap-1">
        <button
          v-tooltip="t('CONVERSATION.AI_FEEDBACK.THUMBS_UP')"
          type="button"
          :aria-label="t('CONVERSATION.AI_FEEDBACK.THUMBS_UP')"
          :aria-pressed="selectedRating === 'up'"
          class="flex items-center justify-center rounded-md size-6 text-sm leading-none hover:bg-n-alpha-2"
          :class="selectedRating === 'up' ? 'bg-n-alpha-2 opacity-100' : 'opacity-60 hover:opacity-100'"
          @click="rate('up')"
        >
          <span aria-hidden="true">👍</span>
        </button>
        <button
          v-tooltip="t('CONVERSATION.AI_FEEDBACK.THUMBS_DOWN')"
          type="button"
          :aria-label="t('CONVERSATION.AI_FEEDBACK.THUMBS_DOWN')"
          :aria-pressed="selectedRating === 'down'"
          class="flex items-center justify-center rounded-md size-6 text-sm leading-none hover:bg-n-alpha-2"
          :class="selectedRating === 'down' ? 'bg-n-alpha-2 opacity-100' : 'opacity-60 hover:opacity-100'"
          @click="rate('down')"
        >
          <span aria-hidden="true">👎</span>
        </button>
        <span
          v-if="ratingStatus === 'saved'"
          class="flex items-center gap-1 ml-1 text-xs text-n-teal-11"
        >
          <Icon icon="i-lucide-check" class="size-[14px]" />
          {{ t('CONVERSATION.AI_FEEDBACK.RATING_SAVED') }}
        </span>
        <span
          v-else-if="ratingStatus === 'error'"
          class="flex items-center gap-1 ml-1 text-xs text-n-ruby-11"
        >
          <Icon icon="i-lucide-alert-triangle" class="size-[14px]" />
          {{ t('CONVERSATION.AI_FEEDBACK.SAVE_ERROR') }}
        </span>
        <span
          v-else-if="ratingStatus === 'saving'"
          class="ml-1 text-xs text-n-slate-11"
        >
          {{ t('CONVERSATION.AI_FEEDBACK.SAVING') }}
        </span>
      </div>
      <!-- digitt: DENG-7353 — copy the bot reply into the composer -->
      <button
        type="button"
        class="flex items-center gap-1 px-2 h-6 rounded-md text-sm font-medium leading-none bg-n-amber-9/10 text-n-amber-12 hover:bg-n-amber-9/20"
        @click="useResponse"
      >
        <Icon icon="i-ph-copy" />
        {{ t('CONVERSATION.AI_FEEDBACK.USE_RESPONSE') }}
      </button>
    </div>
    <template v-if="selectedRating">
      <textarea
        v-model="comment"
        :maxlength="MAX_COMMENT_LENGTH"
        :placeholder="t('CONVERSATION.AI_FEEDBACK.COMMENT_PLACEHOLDER')"
        :aria-label="t('CONVERSATION.AI_FEEDBACK.COMMENT_ARIA')"
        rows="2"
        class="w-full mt-1 reset-base bg-n-alpha-black1 px-2 py-1.5 text-sm border border-dashed border-n-amber-12/40 rounded-md focus:outline-0 focus:border-n-amber-12 resize-none text-n-amber-12 placeholder:text-n-amber-12/50"
      />
      <div class="flex items-center gap-2 mt-1">
        <button
          type="button"
          :disabled="!hasUnsavedComment"
          class="px-2 py-1 text-xs rounded-md text-n-amber-12 bg-n-alpha-2 hover:bg-n-alpha-3 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="saveComment"
        >
          {{ t('CONVERSATION.AI_FEEDBACK.SAVE_COMMENT') }}
        </button>
        <span
          v-if="commentStatus === 'saved' && !hasUnsavedComment"
          class="flex items-center gap-1 text-xs text-n-teal-11"
        >
          <Icon icon="i-lucide-check" class="size-[14px]" />
          {{ t('CONVERSATION.AI_FEEDBACK.COMMENT_SAVED') }}
        </span>
        <span
          v-else-if="commentStatus === 'error'"
          class="flex items-center gap-1 text-xs text-n-ruby-11"
        >
          <Icon icon="i-lucide-alert-triangle" class="size-[14px]" />
          {{ t('CONVERSATION.AI_FEEDBACK.SAVE_ERROR') }}
        </span>
        <span
          v-else-if="commentStatus === 'saving'"
          class="text-xs text-n-slate-11"
        >
          {{ t('CONVERSATION.AI_FEEDBACK.SAVING') }}
        </span>
      </div>
    </template>
  </div>
</template>
